/**
 * NetworkStateManager
 * Encapsulates all offline/online state management and logic.
 * Reduces clutter in MarianaChat component.
 */

export class NetworkStateManager {
  private static readonly VISIBILITY_GRACE_MS = 1500;

  private isOffline = false;
  private hasEstablishedSession = false;
  private hasShownInitialMessage = false;
  private waitingForNetworkToStart = false;
  private isConnecting = false;
  private pageHidden = false;
  private visibleSinceTimestamp = 0;

  constructor() { }

  /**
   * Mark that a session has been successfully established (after onConnected).
   */
  markSessionEstablished(): void {
    this.hasEstablishedSession = true;
    this.waitingForNetworkToStart = false;
    this.isOffline = false;
  }

  /**
   * Mark that the initial message has been shown to the user.
   */
  markInitialMessageShown(): void {
    this.hasShownInitialMessage = true;
  }

  /**
   * Get whether to show initial message (first connection only).
   */
  shouldShowInitialMessage(isReconnecting: boolean): boolean {
    return !isReconnecting && !this.hasShownInitialMessage;
  }

  /**
   * Handle offline event (network went down).
   */
  handleOffline(): { shouldShowMessage: boolean; isPreSession: boolean } {
    this.isOffline = true;

    if (this.hasEstablishedSession) {
      // Active session: show offline message
      return { shouldShowMessage: true, isPreSession: false };
    } else {
      // Pre-session: wait for network before attempting connection
      this.waitingForNetworkToStart = true;
      return { shouldShowMessage: false, isPreSession: true };
    }
  }

  /**
   * Handle online event (network came back).
   * Returns whether to attempt connection.
   */
  handleOnline(): boolean {
    this.isOffline = false;

    if (!this.waitingForNetworkToStart || this.hasEstablishedSession) {
      return false; // No action needed
    }

    // Was waiting for network before session started
    return true; // Try to start connection
  }

  /**
   * Track page hidden state to suppress transient socket errors while not visible.
   */
  handlePageHidden(): void {
    this.pageHidden = true;
  }

  /**
   * Track page visible state and start a short grace period.
   * Only starts grace period when transitioning from hidden to visible (to suppress deferred errors).
   */
  handlePageVisible(): void {
    const wasHidden = this.pageHidden;
    this.pageHidden = false;
    // Only set timestamp when actually transitioning from hidden→visible
    // to avoid suppressing legitimate errors on initial mount
    if (wasHidden) {
      this.visibleSinceTimestamp = Date.now();
    }
  }

  /**
   * Check if browser is currently offline (from our tracking).
   */
  getIsOffline(): boolean {
    return this.isOffline;
  }

  /**
   * Check if session has been established.
   */
  getHasEstablishedSession(): boolean {
    return this.hasEstablishedSession;
  }

  /**
   * Start connection attempt (guard checks).
   * Returns { canStart: boolean, shouldShowOfflineMessage: boolean }
   */
  startConnection(): { canStart: boolean; shouldShowOfflineMessage: boolean } {
    if (this.isConnecting) {
      return { canStart: false, shouldShowOfflineMessage: false };
    }

    if (!navigator.onLine || this.isOffline) {
      this.waitingForNetworkToStart = true;
      return { canStart: false, shouldShowOfflineMessage: true };
    }

    this.isConnecting = true;
    this.waitingForNetworkToStart = false;
    return { canStart: true, shouldShowOfflineMessage: false };
  }

  /**
   * Mark connection attempt as finished.
   */
  finishConnection(success: boolean): void {
    this.isConnecting = false;

    if (!success && !this.hasEstablishedSession && !navigator.onLine) {
      this.waitingForNetworkToStart = true;
    }
  }

  /**
   * Check if can attempt reconnect (from user action or auto-reconnect).
   * Returns { canAttempt: boolean, shouldShowOfflineMessage: boolean }
   */
  attemptReconnect(hasEstablishedSession: boolean): {
    canAttempt: boolean;
    shouldShowOfflineMessage: boolean;
  } {
    if (!navigator.onLine || this.isOffline) {
      if (!hasEstablishedSession) {
        this.waitingForNetworkToStart = true;
      }
      return { canAttempt: false, shouldShowOfflineMessage: true };
    }

    return { canAttempt: true, shouldShowOfflineMessage: false };
  }

  /**
   * Should ignore incoming message (arrived while offline)?
   */
  shouldIgnoreMessage(): boolean {
    return this.isOffline;
  }

  /**
   * Should ignore typing event?
   */
  shouldIgnoreTyping(): boolean {
    return this.isOffline;
  }

  /**
   * Should ignore error event?
   */
  shouldIgnoreError(): boolean {
    if (this.isOffline) return true;
    if (this.pageHidden) return true;

    const elapsedSinceVisible = Date.now() - this.visibleSinceTimestamp;
    return elapsedSinceVisible < NetworkStateManager.VISIBILITY_GRACE_MS;
  }
}
